import { useRouter } from 'shared-routing';
import { Property } from 'shared-types/Property';
import { AccommodationDetailListItem, formatCurrency } from 'voyager';

import { useGetPackagesAvailability } from '@features/packages/hooks/useGetPackagesAvailability';
import { useRates as useRatesWithPackages } from '@features/rates/queries';
import { useRoomPageSettings } from '@features/rooms';
import { TranslationsFunc, useTranslations } from '@lang';
import { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { queryParamToString } from '@utils/queryParam';

import { useAvailableRoom, useRates, useStaticRoom } from '../hooks';
import { Rates } from '../hooks/types';

export const mapRateToDetailItem = (
  rate: Rates[number],
  t: TranslationsFunc,
  numberOfNights: number
): AccommodationDetailListItem => ({
  id: rate.rateId,
  name: rate.rateTitle,
  price: rate.pricePerStay
    ? { amount: rate.pricePerStay.amount, currencyCode: rate.pricePerStay.currencyCode }
    : undefined,
  secondaryPriceLabel:
    rate.pricePerStay && numberOfNights > 0
      ? t('room.pricePerNight', {
          pricePerNight: formatCurrency(
            rate.pricePerStay.amount / numberOfNights,
            rate.pricePerStay.currencyCode
          ),
        })
      : undefined,
});

export function useRoomDetail(
  journeyParams: ParsedJourneyParams,
  roomIdsInBasket: string[],
  language: string,
  property: Property
) {
  const { t } = useTranslations();
  const { query } = useRouter();

  const roomId = queryParamToString(query.id) ?? '';

  const { staticRoom, isFetchingStaticRoom, staticRoomError } = useStaticRoom(roomId, language);
  const { availableRoom, isFetching: isFetchingAvailableRoom } = useAvailableRoom(
    roomId,
    journeyParams
  );
  const { rates, isFetching: isFetchingRates } = useRates(roomId, journeyParams, language);

  const fromDate = journeyParams.fromDate.format('YYYY-MM-DD');
  const toDate = journeyParams.toDate
    ? journeyParams.toDate.format('YYYY-MM-DD')
    : journeyParams.fromDate.clone().add(1, 'days').format('YYYY-MM-DD');
  const numberOfNights = (
    journeyParams.toDate ?? journeyParams.fromDate.clone().add(1, 'days')
  ).diff(journeyParams.fromDate, 'days');

  const { rates: ratesWithPackages, isFetching: isFetchingRatesWithPackages } =
    useRatesWithPackages({
      fromDate,
      toDate,
      adults: journeyParams.adults ?? 0,
      children: journeyParams.children ?? 0,
      childAges: journeyParams.childAges,
      rooms: journeyParams.rooms || [
        { roomNumber: 1, adults: journeyParams.adults ?? 0, children: journeyParams.children ?? 0 },
      ],
      promoRateCode: journeyParams.promoRateCode,
    });
  const packagesByRateId = new Map(
    ratesWithPackages.filter((rate) => rate.package).map((rate) => [rate.id, rate.package])
  );

  const hasPackageData = !isFetchingRatesWithPackages;
  const { data: packagesAvailability, isFetching: isFetchingPackagesAvailability } =
    useGetPackagesAvailability(
      property,
      rates.map((rate) => ({
        id: rate.rateId,
        imageUrl: rate.rateImageUrl,
        description: rate.rateDescription,
        title: rate.rateTitle,
        cancellationPolicyText: null,
        unAvailable: !rate.available,
        package: packagesByRateId.get(rate.rateId) ?? null,
      })),
      journeyParams,
      hasPackageData
    );

  const availableRates = rates.filter(({ available }, index) => {
    const packageAvailability = packagesAvailability?.[index];

    return packageAvailability ? !packageAvailability.isUnavailable : available;
  });

  const numRoomsOfTypeAlreadyInBasket = (roomIdsInBasket ?? []).filter(
    (id) => id === roomId
  ).length;
  const allAvailableRoomsInBasket =
    !!availableRoom?.quantity && numRoomsOfTypeAlreadyInBasket >= availableRoom.quantity;
  const isQuantityEnough =
    !!availableRoom?.quantity && availableRoom.quantity >= (journeyParams.rooms?.length ?? 1);
  const isRoomOutOfStock = allAvailableRoomsInBasket || !isQuantityEnough;

  const { content: roomsContent } = useRoomPageSettings(language, t('common.room'));
  const fallbackImageUrl = roomsContent?.heroBackgroundImage ?? undefined;

  const urgencyLabel =
    availableRoom?.displayUrgencyMessaging && availableRoom.quantity
      ? t('room.hurryMessage', { quantity: availableRoom.quantity })
      : undefined;

  const items = availableRates.map((rate) => mapRateToDetailItem(rate, t, numberOfNights));

  return {
    roomId,
    staticRoom,
    staticRoomError,
    availableRates,
    items,
    fallbackImageUrl,
    urgencyLabel,
    numberOfNights,
    isRoomOutOfStock,
    allAvailableRoomsInBasket,
    isFetching:
      isFetchingRates ||
      isFetchingAvailableRoom ||
      isFetchingRatesWithPackages ||
      isFetchingPackagesAvailability,
    isLoading: isFetchingStaticRoom,
  };
}
